<template>
  <div class="analytics-panel">

    <div class="analytics-section">
      <div class="analytics-section-title">Record Counts</div>
      <div class="stat-cards">
        <div class="stat-card" v-for="ss in spreadsheetList" :key="ss.uniqueTitle">
          <div class="stat-card-value">{{ formatNumber(recordCounts[ss.uniqueTitle] || 0) }}</div>
          <div class="stat-card-label">{{ ss.title || ss.uniqueTitle }}</div>
        </div>
        <div class="stat-card stat-card-total" v-if="spreadsheetList.length > 1">
          <div class="stat-card-value">{{ formatNumber(totalRecords) }}</div>
          <div class="stat-card-label">total records</div>
        </div>
      </div>
    </div>

    <div class="analytics-section">
      <div class="analytics-section-title">CRUD Operations</div>
      <div v-if="crudRows.length === 0" class="analytics-empty">No activity recorded yet.</div>
      <table v-else class="analytics-table">
        <thead>
          <tr>
            <th>spreadsheet</th>
            <th>operation</th>
            <th>count</th>
            <th>avg latency</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in crudRows" :key="i">
            <td>{{ row.entity_name }}</td>
            <td>
              <span class="op-badge" :class="'op-' + opColor(row.event_type)">{{ formatOp(row.event_type) }}</span>
            </td>
            <td>{{ formatNumber(row.count) }}</td>
            <td>{{ row.avg_duration_ms != null ? Math.round(row.avg_duration_ms) + ' ms' : '–' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="analytics-section">
      <div class="analytics-section-title">Activity Over Time</div>
      <div v-if="activityDays.length === 0" class="analytics-empty">No activity recorded yet.</div>
      <div v-else class="histogram-wrap">
        <div class="histogram">
          <div class="histogram-y-axis">
            <span class="histogram-y-label">{{ maxDayCount }}</span>
            <span class="histogram-y-label">{{ Math.round(maxDayCount / 2) }}</span>
            <span class="histogram-y-label">0</span>
          </div>
          <div class="histogram-bars">
            <div class="histogram-col" v-for="day in activityDays" :key="day.date">
              <div class="histogram-stack" :title="day.total + ' total'">
                <div v-if="day.deletes > 0" class="histogram-segment segment-delete" :style="{ height: segmentHeight(day.deletes) }" :title="day.deletes + ' deletes'"></div>
                <div v-if="day.updates > 0" class="histogram-segment segment-update" :style="{ height: segmentHeight(day.updates) }" :title="day.updates + ' updates'"></div>
                <div v-if="day.creates > 0" class="histogram-segment segment-create" :style="{ height: segmentHeight(day.creates) }" :title="day.creates + ' creates'"></div>
                <div v-if="day.reads > 0" class="histogram-segment segment-read" :style="{ height: segmentHeight(day.reads) }" :title="day.reads + ' reads'"></div>
              </div>
              <div class="histogram-x-label">{{ formatDate(day.date) }}</div>
            </div>
          </div>
        </div>
        <div class="chart-legend">
          <span class="legend-item"><span class="legend-dot dot-read"></span> reads</span>
          <span class="legend-item"><span class="legend-dot dot-create"></span> creates</span>
          <span class="legend-item"><span class="legend-dot dot-update"></span> updates</span>
          <span class="legend-item"><span class="legend-dot dot-delete"></span> deletes</span>
        </div>
      </div>
    </div>

  </div>
</template>

<script>

import axios from 'axios'

export default {
  name: 'AnalyticsSpreadsheets',
  props: {
    application: String,
    from: String,
    to: String,
    refreshKey: Number
  },
  data () {
    return {
      crudRows: [],
      activityDays: [],
      recordCounts: {},
      spreadsheetList: []
    }
  },
  computed: {
    totalRecords () {
      return Object.values(this.recordCounts).reduce((a, b) => a + b, 0)
    },
    maxDayCount () {
      if (this.activityDays.length === 0) return 1
      return Math.max(...this.activityDays.map(d => d.total), 1)
    }
  },
  watch: {
    refreshKey: 'loadData'
  },
  mounted () {
    this.loadData()
  },
  methods: {
    async loadData () {

      try {

        const params = {}
        if (this.from) params.from = this.from
        if (this.to) params.to = this.to

        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/analytics/spreadsheets`, {
          headers: { 'application-unique-title': this.application },
          params
        })

        this.crudRows = res.data.crudCounts || []
        this.recordCounts = res.data.recordCounts || {}
        this.spreadsheetList = res.data.spreadsheets || []

        const timeData = res.data.activityOverTime || []
        const byDate = {}

        for (const row of timeData) {

          if (!byDate[row.date]) {
            byDate[row.date] = { reads: 0, creates: 0, updates: 0, deletes: 0, total: 0 }
          }

          const count = parseInt(row.count)
          const et = row.event_type || ''

          if (et.includes('delete')) {
            byDate[row.date].deletes += count
          } else if (et.includes('update')) {
            byDate[row.date].updates += count
          } else if (et.includes('create')) {
            byDate[row.date].creates += count
          } else {
            byDate[row.date].reads += count
          }

          byDate[row.date].total += count
        }

        this.activityDays = Object.entries(byDate)
          .map(([date, data]) => ({ date, ...data }))
          .sort((a, b) => a.date.localeCompare(b.date))

      } catch (err) {
        console.log('failed to load spreadsheet analytics', err)
      }
    },
    formatNumber (n) {
      return Number(n).toLocaleString()
    },
    formatOp (eventType) {
      const map = {
        data_create: 'create',
        data_create_bulk: 'bulk create',
        data_read: 'read',
        data_update: 'update',
        data_update_bulk: 'bulk update',
        data_delete: 'delete',
        data_search: 'search',
        data_link: 'link',
        data_unlink: 'unlink'
      }
      return map[eventType] || eventType
    },
    opColor (eventType) {
      if (eventType.includes('create')) return 'green'
      if (eventType.includes('read') || eventType.includes('search')) return 'blue'
      if (eventType.includes('update')) return 'yellow'
      if (eventType.includes('delete')) return 'red'
      return 'gray'
    },
    formatDate (dateStr) {
      const d = new Date(dateStr + 'T00:00:00')
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },
    segmentHeight (count) {
      if (!this.maxDayCount) return '0%'
      return Math.max((count / this.maxDayCount) * 100, 1) + '%'
    }
  }
}
</script>

<style scoped>

.analytics-panel {
  max-width: 960px;
}

.analytics-section {
  margin-bottom: 28px;
}

.analytics-section-title {
  font-size: 0.78rem;
  font-weight: 600;
  color: #a0a09e;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 10px;
}

.analytics-empty {
  color: #a8a8a4;
  font-size: 0.8rem;
  padding: 12px 0;
}

/* ── Stat cards ── */
.stat-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.stat-card {
  background: #1a1a17;
  border: 1px solid #2a2a27;
  border-radius: 6px;
  padding: 12px 16px;
  min-width: 120px;
}

.stat-card-total {
  border-color: #9ad12b44;
}

.stat-card-value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #e8e8e6;
  line-height: 1.2;
}

.stat-card-total .stat-card-value {
  color: #9ad12b;
}

.stat-card-label {
  font-size: 0.7rem;
  color: #707070;
  margin-top: 2px;
}

/* ── Table ── */
.analytics-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.analytics-table th {
  text-align: left;
  color: #707070;
  font-weight: 500;
  padding: 6px 10px;
  border-bottom: 1px solid #2a2a27;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.analytics-table td {
  padding: 7px 10px;
  color: #a0a09e;
  border-bottom: 1px solid #1e1e1b;
}

.analytics-table tr:hover td {
  background: #1a1a17;
}

.op-badge {
  font-size: 0.68rem;
  padding: 2px 7px;
  border-radius: 3px;
  font-weight: 500;
}

.op-green { color: #6cc644; background: rgba(108, 198, 68, 0.1); }
.op-blue { color: #4a9eff; background: rgba(74, 158, 255, 0.1); }
.op-yellow { color: #e8a020; background: rgba(232, 160, 32, 0.1); }
.op-red { color: #e05050; background: rgba(224, 80, 80, 0.1); }
.op-gray { color: #707070; background: rgba(112, 112, 112, 0.1); }

/* ── Histogram ── */
.histogram-wrap {
  overflow-x: auto;
}

.histogram {
  display: flex;
  align-items: flex-end;
  min-height: 180px;
  gap: 0;
}

.histogram-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  height: 160px;
  padding-right: 8px;
  flex-shrink: 0;
  border-right: 1px solid #2a2a27;
}

.histogram-y-label {
  font-size: 0.62rem;
  color: #a8a8a4;
  line-height: 1;
}

.histogram-bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  flex: 1;
  height: 160px;
  padding-left: 6px;
  border-bottom: 1px solid #2a2a27;
}

.histogram-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 28px;
  max-width: 60px;
  height: 100%;
}

.histogram-stack {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  flex: 1;
}

.histogram-segment {
  width: 100%;
  transition: height 0.3s ease;
  min-height: 0;
}

.histogram-segment:first-child {
  border-radius: 2px 2px 0 0;
}

.segment-read { background: #4a9eff; }
.segment-create { background: #6cc644; }
.segment-update { background: #e8a020; }
.segment-delete { background: #e05050; }

.histogram-x-label {
  font-size: 0.6rem;
  color: #a8a8a4;
  margin-top: 5px;
  white-space: nowrap;
  flex-shrink: 0;
}

.chart-legend {
  display: flex;
  gap: 14px;
  margin-top: 10px;
  padding-left: 40px;
}

.legend-item {
  font-size: 0.68rem;
  color: #707070;
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

.dot-read { background: #4a9eff; }
.dot-create { background: #6cc644; }
.dot-update { background: #e8a020; }
.dot-delete { background: #e05050; }
</style>
